import { motion } from "framer-motion";
import { Globe, CheckCircle, Languages } from "lucide-react";
import { CountryFlags } from "@/components/CountryFlags";

interface TrustSectionProps {
  t: (key: string) => string;
}

const REGION_ICONS = [Globe, Globe, Globe];

export function TrustSection({ t }: TrustSectionProps) {
  const regions = [
    t("home.trustRegion1"),
    t("home.trustRegion2"),
    t("home.trustRegion3"),
  ];

  const badges = [
    { icon: CheckCircle, key: "trustBadge1" },
    { icon: Languages,   key: "trustBadge2" },
    { icon: Globe,       key: "trustBadge3" },
  ];

  return (
    <section className="py-20 border-t border-border/20" data-testid="section-trust">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <motion.h2
          className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4"
          style={{ fontFamily: "'Playfair Display', serif" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="text-trust-title"
        >
          {t("home.trustTitle")}
        </motion.h2>

        <motion.p
          className="text-sm text-muted-foreground max-w-lg mx-auto mb-12"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {t("home.trustSubtitle")}
        </motion.p>

        {/* Regions */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {regions.map((region, i) => {
            const Icon = REGION_ICONS[i];
            return (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-border/40 bg-card/40 text-sm text-muted-foreground"
              >
                <Icon className="w-3.5 h-3.5 text-primary/50 shrink-0" />
                {region}
              </div>
            );
          })}
        </motion.div>

        {/* Credential badges */}
        <motion.div
          className="flex flex-col sm:flex-row justify-center gap-4"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {badges.map(({ icon: Icon, key }, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-5 py-3 rounded-lg border border-primary/15 bg-primary/5 text-sm text-foreground/80 font-medium"
            >
              <Icon className="w-4 h-4 text-primary/60 shrink-0" />
              {t(`home.${key}`)}
            </div>
          ))}
        </motion.div>

        <CountryFlags />
      </div>
    </section>
  );
}
